/**
 * @typedef {Object} VisibleRowsChangePayload
 * @property {number} startIndex
 * @property {number} stopIndex
 * @property {Array<object>} visibleModels
 */

/**
 * @callback OnVisibleRowsChange
 * @param {VisibleRowsChangePayload} payload
 * @returns {void}
 */

/**
 * @typedef {Object} VirtualizedVisibleRowsEmitArgs
 * @property {number} startIndex
 * @property {number} stopIndex
 * @property {object} collection
 * @property {string} [modelField]
 * @property {boolean} [showTotalRow]
 * @property {(index: number) => (object|null)} getGroupedRow
 * @property {number} groupedRowsLength
 */

/**
 * Visible row tracking helper for virtualized tables.
 */
export default class VirtualizedVisibleRowsObserver {
  constructor() {
    this.onVisibleRowsChange = null;
  }

  /**
   * @param {{collection: object, modelField?: string, groupedRowsLength: number}} args
   * @returns {number}
   */
  getRowCountForVisibleModels = ({ collection, modelField, groupedRowsLength }) => {
    if (collection.groupBy) {
      return groupedRowsLength;
    }
    if (modelField) {
      return collection[modelField]?.length || 0;
    }
    return collection.size;
  };

  /**
   * @param {{index: number, collection: object, modelField?: string, showTotalRow?: boolean, getGroupedRow: (index: number) => (object|null), groupedRowsLength: number}} args
   * @returns {object|null}
   */
  getModelForIndex = ({ index, collection, modelField, showTotalRow, getGroupedRow, groupedRowsLength }) => {
    if (showTotalRow && index >= this.getRowCountForVisibleModels({ collection, modelField, groupedRowsLength })) {
      return null;
    }

    if (collection.isTreeGroupBy) {
      const row = getGroupedRow(index);
      return row?.model || null;
    }

    if (collection.groupBy) {
      const row = getGroupedRow(index);
      if (!row || row.isGroupSummary) {
        return null;
      }
      return row.model || null;
    }

    if (modelField) {
      return collection[modelField]?.[index] || null;
    }

    return index < collection.size ? collection.at(index) : null;
  };

  /**
   * @param {{onVisibleRowsChange?: OnVisibleRowsChange}} args
   * @returns {void}
   */
  update = ({ onVisibleRowsChange }) => {
    this.onVisibleRowsChange = onVisibleRowsChange;
  };

  /**
   * @param {VirtualizedVisibleRowsEmitArgs} args
   * @returns {void}
   */
  emitVisibleRows = ({
    startIndex,
    stopIndex,
    collection,
    modelField,
    showTotalRow,
    getGroupedRow,
    groupedRowsLength
  }) => {
    if (!this.onVisibleRowsChange) {
      return;
    }

    const visibleModels = [];
    for (let i = startIndex; i <= stopIndex; i += 1) {
      const model = this.getModelForIndex({
        index: i,
        collection,
        modelField,
        showTotalRow,
        getGroupedRow,
        groupedRowsLength
      });
      if (model) {
        visibleModels.push(model);
      }
    }

    this.onVisibleRowsChange({ startIndex, stopIndex, visibleModels });
  };
}
